import { styled, ThemeProvider } from "@mui/material/styles";
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { Worker } from "@react-pdf-viewer/core";
import { Amplify, Auth } from "aws-amplify";
import { MaterialDesignContent, SnackbarProvider } from "notistack";
import { useEffect, useState } from "react";
import { isMobile } from 'react-device-detect';
import { BrowserRouter as Router } from "react-router-dom";
import { BreakpointProvider } from "react-socks";
import awsConfig from "../aws-exports";
import App from "../components/App";
import MobileApp from "../components/MobileApp";
import PublicApp from "../components/PublicApp";
import AuthProvider from "../contexts/AuthContext";
import DashboardProvider from "../contexts/DashboardContext";
import DashboardsProvider from "../contexts/DashboardsContext";
import FileStructureProvider from "../contexts/FileStructureContext";
import GroupSettingsProvider from "../contexts/GroupSettingsContext";
import InboxProvider from "../contexts/InboxContext";
import SharePointContext from "../contexts/SharepointContext";
import SubscriptionProvider from "../contexts/SubscriptionContext";
import UsersProvider from "../contexts/UsersContext";
import WorkspaceProvider from "../contexts/WorkspaceContext";
import WorkspacesProvider from "../contexts/WorkspacesContext/WorkspacesProvider";
import theme from "../theme";
import TableProvider from "../contexts/TableContext";

Amplify.configure({
  ...awsConfig,
  Storage: {
    bucket: awsConfig.aws_main_bucket,
    region: awsConfig.aws_project_region,
  },
});

const IntegrationErrorContent = styled(MaterialDesignContent)(() => ({
  '&.notistack-MuiContent-error': {
    width: 'fit-content',
    height: 'fit-content',
    padding: '8px 24px 8px 0',
    borderRadius: 16,
    background: '#fff',
    border: `1px solid ${theme.colors.neutral['300']}`,
    boxShadow: theme.shadows[6],
    transition: '0.5s ease-in-out',
    zIndex: 1800,
  },
}));

const AppProviders: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <ThemeProvider theme={theme}>
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
        <SnackbarProvider maxSnack={4} Components={{ error: IntegrationErrorContent }}>
          <BreakpointProvider>
            <SharePointContext>
              <Router>
                {children}
              </Router>
            </SharePointContext>
          </BreakpointProvider>
        </SnackbarProvider>
      </Worker>
    </LocalizationProvider>
  </ThemeProvider>
);

const PrivateProviders: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <AuthProvider>
    <SubscriptionProvider>
      <UsersProvider>
        <FileStructureProvider>
          <GroupSettingsProvider>
            <WorkspacesProvider>
              <WorkspaceProvider>
                <DashboardsProvider>
                  <DashboardProvider>
                    <InboxProvider>
                      <TableProvider>
                        {children}
                      </TableProvider>
                    </InboxProvider>
                  </DashboardProvider>
                </DashboardsProvider>
              </WorkspaceProvider>
            </WorkspacesProvider>
          </GroupSettingsProvider>
        </FileStructureProvider>
        </UsersProvider>
    </SubscriptionProvider>
  </AuthProvider>
);

const PublicProviders: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <WorkspacesProvider>
    <WorkspaceProvider>
      <DashboardsProvider>
        <DashboardProvider>
          {children}
        </DashboardProvider>
      </DashboardsProvider>
    </WorkspaceProvider>
  </WorkspacesProvider>
);

const RootContainer: React.FC<{}> = () => {
  const [loading, setLoading] = useState(true);
  const [loggedIn, setLoggedIn] = useState(false);

  useEffect(() => {
    Auth.currentAuthenticatedUser()
      .then(() => {
        setLoggedIn(true);
        setLoading(false);
      })
      .catch(() => {
        setLoggedIn(false);
        setLoading(false);
      });
  }, []);

  if (isMobile) {
    return (
      <ThemeProvider theme={theme}>
        <Router>
          <MobileApp />
        </Router>
      </ThemeProvider>
    );
  }

  if (loading)
    return (<></>);

  return (
    <AppProviders>
      {loggedIn ? (
        <PrivateProviders>
          <App />
        </PrivateProviders>
      ) : (
        <PublicProviders>
          <PublicApp />
        </PublicProviders>
      )}
    </AppProviders>
  );
};


export default RootContainer;
